import React from 'react'
import {
  FilterSection,
  FilterSubsection,
  FieldLabelWithTooltip,
  AdvancedDropdown,
  type AdvancedDropdownOption,
  ToggleSwitch,
} from '@popety-io/ui'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import type { BuildingSearchFilters } from '../../../Buildings/types/buildingFilters.types'
import { useBuildings } from '../../../Buildings/contexts/BuildingsContext'
import { useBuildingsFavorites } from '@/store/hooks'

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.xsmall} 0;
`

const ToggleLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`

interface UserFiltersProps {
  filters: BuildingSearchFilters
  updateFilter: <K extends keyof BuildingSearchFilters>(
    key: K,
    value: BuildingSearchFilters[K]
  ) => void
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  updateFilter,
}) => {
  const { t } = useTranslation('common')
  const { availableTags, tagsLoading } = useBuildings()
  const { buildingFavorites } = useBuildingsFavorites()

  // Get unique tags from the response and convert to dropdown options
  const uniqueTagsMap = new Map<
    string,
    { id: string; value: string; color: string }
  >()

  // Handle tag structure from context
  if (availableTags && Array.isArray(availableTags)) {
    availableTags.forEach((item: any) => {
      // Only add tags that have a value/label to display
      if (item.value && !uniqueTagsMap.has(item.id)) {
        uniqueTagsMap.set(item.id, {
          id: item.id,
          value: item.value,
          color: item.color || '#4A90E2',
        })
      }
    })
  }

  const uniqueTagOptions: AdvancedDropdownOption[] = Array.from(
    uniqueTagsMap.values()
  ).map((tag) => ({
    id: tag.id,
    label: tag.value,
    icon: (
      <span
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: tag.color,
          display: 'inline-block',
        }}
      />
    ),
  }))

  // Check if favorites filter is active
  const isFavoritesFilterActive =
    filters.building_ids && filters.building_ids.length > 0

  // Extract active favorite building IDs
  const favoriteBuildingIds = buildingFavorites
    .filter((fav) => fav.active)
    .map((fav) => fav.building_id)

  const getUserFiltersCount = () => {
    let count = 0
    if (filters.tags?.length) count++
    if (isFavoritesFilterActive) count++
    return count
  }

  const handleFavoritesToggle = (checked: boolean) => {
    if (checked) {
      // When toggling ON, set building_ids to favorite building IDs
      updateFilter(
        'building_ids',
        favoriteBuildingIds.length > 0 ? favoriteBuildingIds : undefined
      )
    } else {
      // When toggling OFF, clear building_ids filter
      updateFilter('building_ids', undefined)
    }
  }

  const handleTagsChange = (selectedTagIds: string | string[]) => {
    const tagIds = Array.isArray(selectedTagIds)
      ? selectedTagIds
      : [selectedTagIds]

    // Simply update the tags filter with the selected tag IDs
    updateFilter('tags', tagIds.length ? tagIds : undefined)
  }

  // Get currently selected tags
  const selectedTags = filters.tags || []

  return (
    <FilterSection
      title={t('filterSectionTitles.userFilters')}
      icon={
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M20 7h-9m3 10H9m8-5H9m13-3.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm-16 5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm8 5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
        </svg>
      }
      count={getUserFiltersCount() + '/2'}
      active={getUserFiltersCount() > 0}
    >
      <FilterSubsection>
        <FieldLabelWithTooltip
          label={t('filterFieldLabels.favorites')}
          tooltip={t('filterTooltips.building.favorites')}
        />
        <ToggleRow>
          <ToggleLabel>
            {isFavoritesFilterActive
              ? t('filterLabels.showOnlyFavorites')
              : t('filterLabels.showAllBuildings')}
          </ToggleLabel>
          <ToggleSwitch
            checked={isFavoritesFilterActive || false}
            onChange={handleFavoritesToggle}
            size="small"
            disabled={favoriteBuildingIds.length === 0}
          />
        </ToggleRow>
        {favoriteBuildingIds.length === 0 && (
          <div style={{ padding: '8px', color: '#6b6b6b', fontSize: '12px' }}>
            {t('filterMessages.noFavoritesAvailable')}
          </div>
        )}
      </FilterSubsection>
      <FilterSubsection>
        <FieldLabelWithTooltip
          label={t('filterFieldLabels.myTags')}
          tooltip={t('filterTooltips.building.userTags')}
        />
        {tagsLoading ? (
          <div style={{ padding: '8px', color: '#6b6b6b' }}>
            Loading tags...
          </div>
        ) : uniqueTagOptions.length > 0 ? (
          <AdvancedDropdown
            value={selectedTags}
            onChange={handleTagsChange}
            options={uniqueTagOptions}
            placeholder="Select tags"
            multiple
            searchable
            clearable
            size="small"
            width="100%"
            emptyMessage="No tags available"
          />
        ) : (
          <div style={{ padding: '8px', color: '#6b6b6b' }}>
            No tags available. Create tags from the building details panel.
          </div>
        )}
      </FilterSubsection>
    </FilterSection>
  )
}
